// User preferences factory function

import type {
  UserPreferences,
  UserPreferencesAPI,
  UserPreferencesConfig,
  UserAuth,
  Assistant,
  Language,
  DominantHand,
  Theme,
  DragBehavior,
} from "../types/user-types";

// Default values
const DEFAULT_USER_AUTH: UserAuth = {
  id: "",
  isLoggedIn: false,
};

const DEFAULT_ASSISTANT: Assistant = {
  name: "Sketch Assistant",
  personality: "helpful",
};

const DEFAULT_TOOL_PREFERENCES: import("../types/user-types").ToolPreferences =
  {
    handPreference: "right",
    hapticEnabled: true,
    animationsEnabled: true,
    defaultSizes: {},
    defaultInteractionType: "single-click",
    altModePreference: false,
  };

const DEFAULT_USER_PREFERENCES: UserPreferences = {
  user: {
    name: "Anonymous User",
    auth: DEFAULT_USER_AUTH,
    language: "en" as Language,
    dominant_hand: "right" as DominantHand,
    theme: "light" as Theme,
    drag_behavior: "one_finger" as DragBehavior,
    assistant: DEFAULT_ASSISTANT,
    memories_for_llm: [],
    tool_preferences: DEFAULT_TOOL_PREFERENCES,
  },
};

/**
 * Creates a user preferences manager with persistence and validation
 *
 * @param config - Configuration options for preferences management
 * @returns User preferences API with get/set methods
 *
 * @example
 * ```typescript
 * const userPrefs = createUserPreferences({
 *   autoSave: true,
 *   storageKey: 'sketch-user-prefs',
 *   onUpdate: (prefs) => console.log('Preferences updated:', prefs)
 * });
 *
 * userPrefs.updateUser({ name: 'Alex', theme: 'dark' });
 * userPrefs.destroy(); // Always call when done
 * ```
 */
export const createUserPreferences = (
  config: UserPreferencesConfig = {}
): UserPreferencesAPI => {
  const {
    initialData = {},
    onUpdate,
    autoSave = false,
    storageKey = "sketch-user-preferences",
  } = config;

  // Merge initial data with defaults
  let preferences: UserPreferences = {
    user: {
      ...DEFAULT_USER_PREFERENCES.user,
      ...initialData.user,
      auth: {
        ...DEFAULT_USER_PREFERENCES.user.auth,
        ...initialData.user?.auth,
      },
      assistant: {
        ...DEFAULT_USER_PREFERENCES.user.assistant,
        ...initialData.user?.assistant,
      },
    },
  };

  // Load from localStorage if available
  if (typeof window !== "undefined" && autoSave) {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        preferences = {
          user: {
            ...preferences.user,
            ...parsed.user,
          },
        };
      }
    } catch (error) {
      console.warn("Failed to load user preferences from storage:", error);
    }
  }

  // Helper to trigger updates and persistence
  const notifyUpdate = () => {
    onUpdate?.(preferences);

    if (autoSave && typeof window !== "undefined") {
      try {
        localStorage.setItem(storageKey, JSON.stringify(preferences));
      } catch (error) {
        console.warn("Failed to save user preferences to storage:", error);
      }
    }
  };

  // Public API
  const getPreferences = (): UserPreferences => ({
    user: { ...preferences.user },
  });

  const updateUser = (updates: Partial<UserPreferences["user"]>) => {
    preferences = {
      user: {
        ...preferences.user,
        ...updates,
      },
    };
    notifyUpdate();
  };

  const updateAuth = (auth: Partial<UserAuth>) => {
    preferences = {
      user: {
        ...preferences.user,
        auth: {
          ...preferences.user.auth,
          ...auth,
        },
      },
    };
    notifyUpdate();
  };

  const updateAssistant = (assistant: Partial<Assistant>) => {
    preferences = {
      user: {
        ...preferences.user,
        assistant: {
          ...preferences.user.assistant,
          ...assistant,
        },
      },
    };
    notifyUpdate();
  };

  const addMemory = (memory: string) => {
    if (memory.trim() && !preferences.user.memories_for_llm.includes(memory)) {
      preferences = {
        user: {
          ...preferences.user,
          memories_for_llm: [...preferences.user.memories_for_llm, memory],
        },
      };
      notifyUpdate();
    }
  };

  const removeMemory = (memory: string) => {
    preferences = {
      user: {
        ...preferences.user,
        memories_for_llm: preferences.user.memories_for_llm.filter(
          (m) => m !== memory
        ),
      },
    };
    notifyUpdate();
  };

  const updateToolPreferences = (
    toolPrefs: Partial<import("../types/user-types").ToolPreferences>
  ) => {
    preferences = {
      user: {
        ...preferences.user,
        tool_preferences: {
          ...preferences.user.tool_preferences,
          ...toolPrefs,
        },
      },
    };
    notifyUpdate();
  };

  const reset = () => {
    preferences = { ...DEFAULT_USER_PREFERENCES };
    notifyUpdate();
  };

  const serialize = (): string => {
    return JSON.stringify(preferences);
  };

  const deserialize = (data: string) => {
    try {
      const parsed = JSON.parse(data);
      preferences = {
        user: {
          ...DEFAULT_USER_PREFERENCES.user,
          ...parsed.user,
        },
      };
      notifyUpdate();
    } catch (error) {
      console.error("Failed to deserialize user preferences:", error);
    }
  };

  const destroy = () => {
    // Cleanup any resources if needed
    // Currently no cleanup required for this implementation
  };

  return {
    getPreferences,
    updateUser,
    updateAuth,
    updateAssistant,
    updateToolPreferences,
    addMemory,
    removeMemory,
    reset,
    serialize,
    deserialize,
    destroy,
  };
};
